/**
 * <class-single-modal>
 * Variante de <booking-modal> para un producto de un solo paquete de clases
 * que se quiere destacar con su propia tarjeta (ej. Paquete Amigos), en vez
 * de aparecer como una opción más dentro del modal de 6 paquetes.
 *
 * A diferencia de <appointment-modal> (que reserva contra la disponibilidad
 * de Appointments de un staff), este componente reserva contra el
 * **calendario real de clases** (/api/classes), igual que <booking-modal>
 * — porque el producto que abre este modal es, en Mindbody, un paquete de
 * clases más (Program "Signature Group Session"), no un Appointment con
 * horario propio.
 *
 * Reutiliza el mismo CSS (clases .booking-modal__*) y el mismo patrón de
 * apertura genérica por data-open-modal que <appointment-modal>.
 */
if (!customElements.get('class-single-modal')) {
  customElements.define(
    'class-single-modal',
    class ClassSingleModal extends HTMLElement {
      constructor() {
        super();
        this.currentStep = 1;
        // Algunos servicios se agendan manualmente (coordinación directa con el
        // estudio) en vez de mostrar el calendario de clases — en ese caso se omite
        // el paso 2 y el flujo pasa directo de confirmar producto a resumen/compra.
        this.skipScheduling = this.dataset.skipScheduling === 'true';
        this.stepSequence = this.skipScheduling ? [1, 3] : [1, 2, 3];
        this.totalSteps = this.stepSequence.length;
        this.selectedOptions = [];
        this.selectedProductId = null;
        this.selectedVariantId = null;
        this.selectedPricingOptionId = null;
        this.cartItemKey = null;
        this.selectedDate = null;
        this.selectedTime = null; // Objeto Class real de Mindbody (Id, StartDateTime, EndDateTime, ClassScheduleId)
        this.serviceCategory = this.dataset.serviceCategory || 'Reserva';
        this.currentMonth = new Date();
        this.maxMonthsAhead = 3;
        this.apiBaseUrl = this.dataset.apiBaseUrl || 'http://localhost:3000';
        this.schedules = [];
        this.selectedDateSchedules = [];
        this.minAdvanceBookingMinutes = parseInt(this.dataset.minAdvanceBookingMinutes) || 120;
        this.ghostClientId = null;
      }

      connectedCallback() {
        this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this));
        this.querySelectorAll('[data-close-modal]').forEach((el) => el.addEventListener('click', this.hide.bind(this)));
        this.querySelectorAll('[data-cancel-booking]').forEach((btn) => btn.addEventListener('click', this.cancelBooking.bind(this)));
        this.querySelectorAll('[data-continue-step]').forEach((btn) => btn.addEventListener('click', this.nextStep.bind(this)));
        this.querySelectorAll('[data-back-step]').forEach((btn) => btn.addEventListener('click', this.previousStep.bind(this)));
        this.querySelector('[data-go-to-payment]')?.addEventListener('click', this.goToPayment.bind(this));
        this.querySelector('[data-continue-shopping]')?.addEventListener('click', this.continueShopping.bind(this));

        // Auto-seleccionar la única opción disponible (un solo producto)
        const optionInput = this.querySelector('.booking-modal__option-input');
        if (optionInput) {
          optionInput.checked = true;
          this.handleOptionChange({ target: optionInput });
        }
        this.querySelectorAll('.booking-modal__option-input').forEach((input) => {
          input.addEventListener('change', this.handleOptionChange.bind(this));
        });

        this.querySelector('[data-calendar-prev]')?.addEventListener('click', () => this.changeMonth(-1));
        this.querySelector('[data-calendar-next]')?.addEventListener('click', () => this.changeMonth(1));

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });

        this.initializeCalendar();
        this.updateProgressBar();
        this.setupModalOpeners();
      }

      // Los botones que abren ESTE modal llevan data-open-modal="<id de este modal>"
      setupModalOpeners() {
        const selector = `[data-open-modal="${this.id}"]`;

        const bind = (button) => {
          if (button.dataset.classSingleModalBound) return;
          button.dataset.classSingleModalBound = 'true';
          button.addEventListener('click', (e) => {
            e.preventDefault();
            this.show(button);
          });
        };

        this.querySelectorAll(selector).forEach(bind);
        document.querySelectorAll(selector).forEach(bind);

        document.addEventListener('click', (e) => {
          const target = e.target.closest(selector);
          // Ignorar botones ya vinculados arriba: si no, este listener delegado
          // en document también recibe el evento por bubbling y show() se
          // dispara dos veces por el mismo click.
          if (target && !target.dataset.classSingleModalBound) {
            e.preventDefault();
            this.show(target);
          }
        });
      }

      show(opener) {
        this.openedBy = opener;
        this.querySelectorAll('.booking-modal__step').forEach((el) => el.classList.remove('booking-modal__step--active'));
        const step1 = this.querySelector('[data-step="1"]');
        if (step1) step1.classList.add('booking-modal__step--active');
        this.currentStep = 1;
        this.updateProgressBar();
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        this.trapFocus();
      }

      hide() {
        document.body.classList.remove('overflow-hidden');
        document.body.dispatchEvent(new CustomEvent('modalClosed'));
        this.removeAttribute('open');
        this.removeTrapFocus();
      }

      updateProgressBar() {
        const stepIndex = this.stepSequence.indexOf(this.currentStep) + 1;
        const progress = Math.floor((stepIndex / this.totalSteps) * 100);
        const progressFill = this.querySelector('[data-progress-fill]');
        const progressText = this.querySelector('[data-progress-text]');
        if (progressFill) {
          progressFill.style.display = 'block';
          progressFill.style.setProperty('--progress-width', `${progress}%`);
        }
        if (progressText) progressText.textContent = `${progress}%`;
        const progressBar = this.querySelector('.booking-modal__progress-bar');
        if (progressBar) progressBar.style.display = 'flex';
      }

      handleOptionChange(event) {
        const input = event.target;
        if (input.checked) {
          this.selectedOptions = [input.value];
          this.selectedProductId = input.dataset.productId || null;
          this.selectedVariantId = input.dataset.variantId || null;
          this.selectedPricingOptionId = input.dataset.pricingOptionId || null;
        }
      }

      setButtonLoading(button, isLoading, loadingText = null) {
        if (!button) return;
        if (isLoading) {
          if (!button.dataset.originalText) button.dataset.originalText = button.textContent.trim();
          button.disabled = true;
          button.classList.add('booking-modal__button--loading');
          button.textContent = loadingText || button.dataset.loadingText || 'Cargando...';
        } else {
          button.textContent = button.dataset.originalText || 'Continuar';
          button.disabled = false;
          button.classList.remove('booking-modal__button--loading');
        }
      }

      async nextStep() {
        if (this.currentStep === 1 && this.selectedOptions.length === 0) {
          this.showStepError();
          return;
        }
        if (this.currentStep === 2 && (!this.selectedDate || !this.selectedTime)) {
          this.showStepError('Por favor selecciona una fecha y hora');
          return;
        }

        const currentStepElement = this.querySelector(`[data-step="${this.currentStep}"]`);
        const nextStepNumber = this.stepSequence[this.stepSequence.indexOf(this.currentStep) + 1];
        const nextStepElement = nextStepNumber ? this.querySelector(`[data-step="${nextStepNumber}"]`) : null;
        if (!nextStepElement) return;

        currentStepElement.classList.add('booking-modal__step--fade-out-right');

        setTimeout(async () => {
          currentStepElement.classList.remove('booking-modal__step--active', 'booking-modal__step--fade-out-right');
          nextStepElement.classList.add('booking-modal__step--active', 'booking-modal__step--fade-in-right');

          this.currentStep = nextStepNumber;
          this.updateProgressBar();

          if (this.currentStep === 2) {
            this.updateStep2ButtonState();
            this.loadSchedules();
          }
          if (this.currentStep === 3) {
            this.updateSummary();
          }

          setTimeout(() => {
            nextStepElement.classList.remove('booking-modal__step--fade-in-right');
          }, 300);
        }, 300);
      }

      previousStep() {
        if (this.currentStep === 1) return;
        const prevStepNumber = this.stepSequence[this.stepSequence.indexOf(this.currentStep) - 1];
        const currentStepElement = this.querySelector(`[data-step="${this.currentStep}"]`);
        const prevStepElement = prevStepNumber ? this.querySelector(`[data-step="${prevStepNumber}"]`) : null;
        if (!prevStepElement) return;

        currentStepElement.classList.add('booking-modal__step--fade-out-left');

        setTimeout(() => {
          currentStepElement.classList.remove('booking-modal__step--active', 'booking-modal__step--fade-out-left');
          prevStepElement.classList.add('booking-modal__step--active', 'booking-modal__step--fade-in-left');
          this.currentStep = prevStepNumber;
          this.updateProgressBar();
          if (this.currentStep === 2) this.updateStep2ButtonState();
          setTimeout(() => prevStepElement.classList.remove('booking-modal__step--fade-in-left'), 300);
        }, 300);
      }

      updateStep2ButtonState() {
        const step2 = this.querySelector('[data-step="2"]');
        const continueButton = step2 ? step2.querySelector('[data-continue-step]') : null;
        if (continueButton) {
          continueButton.disabled = !(this.selectedDate && this.selectedTime);
        }
      }

      showStepError(message) {
        console.warn(message || 'Error en el paso actual');
      }

      // ============================================
      // CALENDARIO (idéntico a booking-modal.js / appointment-modal.js)
      // ============================================

      initializeCalendar() {
        this.renderCalendar();
      }

      changeMonth(direction) {
        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        const newDate = new Date(Date.UTC(year, month + direction, 1, 12, 0, 0));
        const today = new Date();
        today.setUTCHours(12, 0, 0, 0);
        const maxDate = new Date(today);
        maxDate.setUTCMonth(maxDate.getUTCMonth() + this.maxMonthsAhead);

        if (newDate < today) this.currentMonth = new Date(today);
        else if (newDate > maxDate) this.currentMonth = new Date(maxDate);
        else this.currentMonth = newDate;

        this.renderCalendar();
      }

      renderCalendar() {
        const monthElement = this.querySelector('[data-calendar-month]');
        const daysElement = this.querySelector('[data-calendar-days]');
        if (!monthElement || !daysElement) return;

        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        const monthNames = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
        monthElement.textContent = `${monthNames[month]} ${year}`;

        const firstDay = new Date(Date.UTC(year, month, 1, 12, 0, 0));
        const lastDay = new Date(Date.UTC(year, month + 1, 0, 12, 0, 0));
        const daysInMonth = lastDay.getUTCDate();
        const startingDayOfWeek = firstDay.getUTCDay();

        daysElement.innerHTML = '';

        const today = new Date();
        today.setUTCHours(12, 0, 0, 0);
        const todayYear = today.getUTCFullYear();
        const todayMonth = today.getUTCMonth();
        const todayDate = today.getUTCDate();

        if (startingDayOfWeek > 0) {
          const prevMonthLastDay = new Date(Date.UTC(year, month, 0, 12, 0, 0));
          const daysInPrevMonth = prevMonthLastDay.getUTCDate();
          const prevMonthYear = month === 0 ? year - 1 : year;
          const prevMonth = month === 0 ? 11 : month - 1;

          for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            const dayNumber = daysInPrevMonth - i;
            const prevMonthDate = new Date(Date.UTC(prevMonthYear, prevMonth, dayNumber, 12, 0, 0));
            const dayElement = document.createElement('button');
            dayElement.type = 'button';
            dayElement.className = 'booking-modal__calendar-day booking-modal__calendar-day--prev-month';
            dayElement.textContent = dayNumber;
            const dateStr = `${prevMonthYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(dayNumber).padStart(2, '0')}`;
            dayElement.dataset.date = dateStr;

            const isPast = prevMonthDate < today;
            const lastDayOfPrevMonth = new Date(Date.UTC(prevMonthYear, prevMonth + 1, 0, 12, 0, 0));
            const prevMonthHasFutureDays = lastDayOfPrevMonth >= today;

            if (isPast && !prevMonthHasFutureDays) {
              dayElement.disabled = true;
            } else {
              dayElement.addEventListener('click', () => {
                this.currentMonth = new Date(Date.UTC(prevMonthYear, prevMonth, 1, 12, 0, 0));
                this.renderCalendar();
                if (!isPast) setTimeout(() => this.selectDate(prevMonthDate), 0);
              });
            }
            daysElement.appendChild(dayElement);
          }
        }

        for (let day = 1; day <= daysInMonth; day++) {
          const date = new Date(Date.UTC(year, month, day, 12, 0, 0));
          const dayElement = document.createElement('button');
          dayElement.type = 'button';
          dayElement.className = 'booking-modal__calendar-day';
          dayElement.textContent = day;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          dayElement.dataset.date = dateStr;

          const isPast = year < todayYear || (year === todayYear && month < todayMonth) ||
            (year === todayYear && month === todayMonth && day < todayDate);

          if (isPast) {
            dayElement.classList.add('booking-modal__calendar-day--past');
            dayElement.disabled = true;
          } else {
            const hasAvailability = this.checkDateAvailability(date);
            if (!hasAvailability) {
              dayElement.classList.add('booking-modal__calendar-day--past');
              dayElement.disabled = true;
            } else {
              dayElement.addEventListener('click', () => this.selectDate(date));
            }
          }

          if (this.selectedDate && this.isSameDay(date, this.selectedDate)) {
            dayElement.classList.add('booking-modal__calendar-day--selected');
          }
          daysElement.appendChild(dayElement);
        }

        const totalDaysShown = (startingDayOfWeek > 0 ? startingDayOfWeek : 0) + daysInMonth;
        const daysToCompleteWeek = 7 - (totalDaysShown % 7);
        if (daysToCompleteWeek < 7) {
          for (let day = 1; day <= daysToCompleteWeek; day++) {
            const nextMonthYear = month === 11 ? year + 1 : year;
            const nextMonth = month === 11 ? 0 : month + 1;
            const nextMonthDate = new Date(Date.UTC(nextMonthYear, nextMonth, day, 12, 0, 0));
            const dayElement = document.createElement('button');
            dayElement.type = 'button';
            dayElement.className = 'booking-modal__calendar-day booking-modal__calendar-day--next-month';
            dayElement.textContent = day;
            const dateStr = `${nextMonthYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            dayElement.dataset.date = dateStr;

            const maxDate = new Date(today);
            maxDate.setUTCMonth(maxDate.getUTCMonth() + this.maxMonthsAhead);
            const isWithinRange = nextMonthDate <= maxDate;

            if (isWithinRange) {
              dayElement.addEventListener('click', () => {
                this.currentMonth = new Date(Date.UTC(nextMonthYear, nextMonth, 1, 12, 0, 0));
                this.renderCalendar();
                setTimeout(() => this.selectDate(nextMonthDate), 0);
              });
            } else {
              dayElement.disabled = true;
              dayElement.classList.add('booking-modal__calendar-day--past');
            }
            daysElement.appendChild(dayElement);
          }
        }

        const prevButton = this.querySelector('[data-calendar-prev]');
        const nextButton = this.querySelector('[data-calendar-next]');
        if (prevButton) {
          const minDate = new Date(today);
          prevButton.disabled = this.isSameMonth(this.currentMonth, minDate);
        }
        if (nextButton) {
          const maxDate = new Date(today);
          maxDate.setUTCMonth(maxDate.getUTCMonth() + this.maxMonthsAhead);
          nextButton.disabled = this.isSameMonth(this.currentMonth, maxDate);
        }
      }

      isSameDay(date1, date2) {
        if (!date1 || !date2) return false;
        return date1.getUTCFullYear() === date2.getUTCFullYear() &&
          date1.getUTCMonth() === date2.getUTCMonth() &&
          date1.getUTCDate() === date2.getUTCDate();
      }

      isSameMonth(date1, date2) {
        if (!date1 || !date2) return false;
        return date1.getUTCFullYear() === date2.getUTCFullYear() && date1.getUTCMonth() === date2.getUTCMonth();
      }

      checkDateAvailability(date) {
        const dateStr = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
        return this.schedules.some((schedule) => {
          if (!schedule.StartDateTime) return false;
          // schedule.StartDateTime es un string "naive" de Mindbody (sin Z), ej
          // "2026-09-30T19:00:00". Tomar los primeros 10 caracteres evita el bug
          // de parsear con `new Date()` + leer con getUTC*: eso reinterpreta la
          // hora local como si fuera un instante UTC, y para horarios desde las
          // ~7pm el offset de Panamá (UTC-5) cruza medianoche y el slot se cuela
          // en el día calendario siguiente.
          const scheduleStr = schedule.StartDateTime.slice(0, 10);
          return scheduleStr === dateStr;
        });
      }

      async selectDate(date) {
        this.selectedDate = date;
        this.selectedTime = null;

        const step2 = this.querySelector('[data-step="2"]');
        const continueButton = step2 ? step2.querySelector('[data-continue-step]') : null;
        if (continueButton) continueButton.disabled = true;

        this.querySelectorAll('.booking-modal__calendar-day').forEach((day) => day.classList.remove('booking-modal__calendar-day--selected'));
        const dateStr = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
        const selectedDay = this.querySelector(`[data-date="${dateStr}"]`);
        if (selectedDay) selectedDay.classList.add('booking-modal__calendar-day--selected');

        await this.loadTimeSlots(date);
      }

      async loadTimeSlots(date) {
        const timeSelection = this.querySelector('[data-time-selection]');
        const timeSlots = this.querySelector('[data-time-slots]');
        if (!timeSelection || !timeSlots) return;

        const dateStr = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

        this.selectedDateSchedules = this.schedules.filter((schedule) => {
          if (!schedule.StartDateTime) return false;
          // Ver comentario en checkDateAvailability(): comparar el string
          // directamente evita el bug de cruce de medianoche por UTC-5.
          const scheduleStr = schedule.StartDateTime.slice(0, 10);
          return scheduleStr === dateStr;
        });

        if (this.selectedDateSchedules.length === 0) {
          timeSelection.style.display = 'none';
          return;
        }

        timeSelection.style.display = 'block';
        timeSlots.innerHTML = '';

        const uniqueSchedules = [];
        const seen = new Set();
        this.selectedDateSchedules.forEach((schedule) => {
          const key = `${schedule.StartDateTime}_${schedule.Id || schedule.ClassScheduleId}`;
          if (!seen.has(key)) {
            seen.add(key);
            uniqueSchedules.push(schedule);
          }
        });

        const sortedSchedules = uniqueSchedules.sort((a, b) => new Date(a.StartDateTime) - new Date(b.StartDateTime));

        const now = new Date();
        const minBookingTime = new Date(now.getTime() + this.minAdvanceBookingMinutes * 60 * 1000);
        const availableSchedules = sortedSchedules.filter((schedule) => new Date(schedule.StartDateTime) >= minBookingTime);

        if (availableSchedules.length === 0) {
          timeSelection.style.display = 'none';
          return;
        }

        availableSchedules.forEach((schedule) => {
          try {
            const startTime = new Date(schedule.StartDateTime);
            const endTime = new Date(schedule.EndDateTime);
            if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return;

            const timeChip = document.createElement('button');
            timeChip.type = 'button';
            timeChip.className = 'booking-modal__time-chip';
            timeChip.textContent = `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
            timeChip.dataset.scheduleId = schedule.Id || schedule.ClassScheduleId || '';

            if (this.selectedTime &&
                (this.selectedTime.Id === schedule.Id || this.selectedTime.ClassScheduleId === schedule.ClassScheduleId) &&
                this.selectedTime.StartDateTime === schedule.StartDateTime) {
              timeChip.classList.add('booking-modal__time-chip--selected');
            }

            timeChip.addEventListener('click', () => this.selectTime(schedule, timeChip));
            timeSlots.appendChild(timeChip);
          } catch (error) {
            console.warn('Error al crear chip de tiempo:', error, schedule);
          }
        });

        this.updateStep2ButtonState();
      }

      selectTime(schedule, chipElement) {
        this.selectedTime = schedule;
        this.querySelectorAll('.booking-modal__time-chip').forEach((chip) => chip.classList.remove('booking-modal__time-chip--selected'));
        chipElement.classList.add('booking-modal__time-chip--selected');
        this.updateStep2ButtonState();
      }

      formatTime(date) {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : '';
        return `${displayHours}${displayMinutes} ${ampm}`;
      }

      formatDate(date) {
        const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
        return `${days[date.getDay()]}, ${date.getDate()} de ${months[date.getMonth()]} de ${date.getFullYear()}`;
      }

      updateSummary() {
        const experienceElement = this.querySelector('[data-summary-experience]');
        if (experienceElement) experienceElement.textContent = this.serviceCategory;

        const dateTimeElement = this.querySelector('[data-summary-date-time]');
        if (dateTimeElement && this.selectedDate && this.selectedTime) {
          try {
            const dateFormatted = this.formatDate(new Date(this.selectedDate));
            const timeFormatted = this.formatTime(new Date(this.selectedTime.StartDateTime));
            dateTimeElement.textContent = `${dateFormatted} - ${timeFormatted}`;
          } catch (error) {
            dateTimeElement.textContent = '-';
          }
        }

        const packageElement = this.querySelector('[data-summary-package]');
        if (packageElement) {
          const optionTitle = this.querySelector('.booking-modal__option-title')?.textContent?.trim();
          packageElement.textContent = optionTitle || this.serviceCategory;
        }

        const totalElement = this.querySelector('[data-summary-total]');
        if (totalElement && this.selectedProductId) {
          const selectedInput = this.querySelector(`[data-product-id="${this.selectedProductId}"]`);
          const priceElement = selectedInput?.closest('.booking-modal__option-card')?.querySelector('[data-product-price]');
          totalElement.textContent = priceElement?.textContent?.trim() || '';
        }
      }

      // ============================================
      // API INTEGRATION
      // ============================================

      /**
       * Carga clases reales de Mindbody (/api/classes) — mismo endpoint y misma
       * lógica que <booking-modal>, ya que este producto se reserva contra el
       * calendario normal de clases, no contra disponibilidad de Appointments.
       */
      async loadSchedules() {
        const calendarElement = this.querySelector('[data-calendar]');
        const calendarLoader = this.querySelector('[data-calendar-loader]');
        if (calendarElement) calendarElement.classList.add('booking-modal__calendar--loading');
        if (calendarLoader) calendarLoader.classList.add('booking-modal__calendar-loader--visible');

        try {
          const today = new Date();
          const endDate = new Date(today);
          endDate.setMonth(endDate.getMonth() + this.maxMonthsAhead);

          const response = await fetch(
            `${this.apiBaseUrl}/api/classes?startDate=${today.toISOString()}&endDate=${endDate.toISOString()}`
          );
          if (!response.ok) throw new Error('Error al cargar horarios');

          const data = await response.json();
          if (data.success && data.data && Array.isArray(data.data.Classes)) {
            this.schedules = data.data.Classes;
          } else {
            console.warn('Estructura de datos inesperada:', data.data);
            this.schedules = [];
          }
          this.renderCalendar();
        } catch (error) {
          console.error('Error cargando schedules:', error);
          this.schedules = [];
          this.renderCalendar();
        } finally {
          if (calendarElement) calendarElement.classList.remove('booking-modal__calendar--loading');
          if (calendarLoader) calendarLoader.classList.remove('booking-modal__calendar-loader--visible');
        }
      }

      async goToPayment() {
        const paymentButton = this.querySelector('[data-go-to-payment]');
        if (paymentButton) {
          const loadingText = paymentButton.dataset.loadingText || 'Agregando al carrito...';
          this.setButtonLoading(paymentButton, true, loadingText);
        }
        try {
          await this.addProductToCart();
          window.location.href = '/checkout';
        } catch (error) {
          console.error('Error agregando producto al carrito:', error);
          if (paymentButton) this.setButtonLoading(paymentButton, false);
          alert('Error al agregar el producto al carrito. Por favor intenta de nuevo.');
        }
      }

      async continueShopping() {
        const continueShoppingButton = this.querySelector('[data-continue-shopping]');
        if (continueShoppingButton) continueShoppingButton.disabled = true;
        try {
          await this.addProductToCart();
          this.hide();
        } catch (error) {
          console.error('Error agregando producto al carrito:', error);
          if (continueShoppingButton) continueShoppingButton.disabled = false;
          alert('Error al agregar el producto al carrito. Por favor intenta de nuevo.');
        }
      }

      cancelBooking() {
        this.hide();
      }

      async addProductToCart() {
        if (!this.selectedProductId || !this.selectedVariantId) {
          console.warn('No hay producto o variant seleccionado para agregar al carrito');
          return;
        }

        if (this.skipScheduling) {
          // Sin calendario: no hay clase que bloquear en Mindbody, la activación
          // se coordina manualmente con el estudio.
          const properties = {};
          if (this.dataset.whatsappInstruction) {
            properties['Coordinación de activación'] = this.dataset.whatsappInstruction;
          }
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: this.selectedVariantId,
              quantity: 1,
              properties,
            }),
          });
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          await response.json();

          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            const addedItem = cart.items.find((item) => item.variant_id === this.selectedVariantId);
            if (addedItem) this.cartItemKey = addedItem.key;
          }
          return;
        }

        if (!this.selectedTime || !this.selectedDate) {
          throw new Error('Por favor selecciona una fecha y hora antes de continuar');
        }

        let ghostClientId = null;
        try {
          const ghostResponse = await fetch(`${this.apiBaseUrl}/api/bookings/create-ghost`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              class_id: this.selectedTime.Id || this.selectedTime.ClassScheduleId,
              class_start_datetime: this.selectedTime.StartDateTime,
              cart_item_key: `temp-${Date.now()}`,
              pricing_option_id: this.selectedPricingOptionId || null,
            }),
          });
          if (ghostResponse.ok) {
            const ghostData = await ghostResponse.json();
            if (ghostData.success && ghostData.data) {
              ghostClientId = ghostData.data.ghostClientId;
              this.ghostClientId = ghostClientId;
            }
          } else {
            console.warn('⚠️ No se pudo crear ghost booking, continuando sin bloqueo');
          }
        } catch (ghostError) {
          console.warn('⚠️ Error creando ghost booking:', ghostError);
        }

        // Las que empiezan con "_" son técnicas (uso interno del webhook) y Shopify
        // las oculta automáticamente en carrito, checkout y emails; Fecha/Hora son
        // las que ve el cliente.
        const classProperties = {
          '_Mindbody Class ID': this.selectedTime.Id || this.selectedTime.ClassScheduleId || '',
          '_Class Start DateTime': this.selectedTime.StartDateTime || '',
          '_Class End DateTime': this.selectedTime.EndDateTime || '',
          '_Class Schedule ID': this.selectedTime.ClassScheduleId || '',
          '_Mindbody Pricing Option ID': this.selectedPricingOptionId || '',
        };
        if (ghostClientId) classProperties['_Ghost Client ID'] = ghostClientId.toString();

        try {
          classProperties['Fecha'] = this.formatDate(new Date(this.selectedDate));
          classProperties['Hora'] = this.formatTime(new Date(this.selectedTime.StartDateTime));
        } catch (error) {
          console.warn('Error formateando fecha/hora:', error);
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.selectedVariantId,
            quantity: 1,
            properties: classProperties,
          }),
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        await response.json();

        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          const addedItem = cart.items.find((item) => item.variant_id === this.selectedVariantId);
          if (addedItem) this.cartItemKey = addedItem.key;
        }
      }

      trapFocus() {
        const focusableElements = this.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        this.addEventListener('keydown', this.handleTrapFocus);
        firstElement?.focus();
      }

      handleTrapFocus = (event) => {
        if (event.code !== 'Tab') return;
        const focusableElements = this.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      };

      removeTrapFocus() {
        this.removeEventListener('keydown', this.handleTrapFocus);
        this.openedBy?.focus();
      }
    }
  );
}
